import React, {Fragment, useEffect, useRef, useState} from 'react';
import moment from 'moment'
import momentTimezone from 'moment-timezone'
import '../graphmodal/graphmodal.css';
import toast from '../../components/toast'
import Graph from './Graph';
import sensorService from '../../services/sensorService';
import constants from '../../config/constants'
import LoadingWheel from '../../components/loadingwheel'
import settingsService from '../../services/settingsService';
import pinConfigurationService from '../../services/pinConfigurationService';
import temperatureService from '../../services/temperatureService';
import Select from '../select';
import {Checkbox, DatePicker, Tooltip} from 'antd';
import _ from "lodash";
import {
  formatLegendLabel,
  pinConfigMapByRepresentation,
  pinTypeAxisTitle,
  resolvePlotUnit,
} from '../../helpers/graphSensorUnit';
import { clampEndDateForStart, clampStartDateForEnd, isGraphDateRangeValid } from '../../helpers/graphDateRange';

const {RangePicker} = DatePicker;
const {dateError} = constants.message

const filterConfigHistoryByCommissionedDate = (
    configHistory: any[] = [],
    commissionedDate?: number
  ) => {
    if (!commissionedDate) {
      return configHistory;
    }
  
    const filtered = configHistory.filter((config: any) => {
      if (!config?.start_date) {
        return true;
      }
      return Number(config.start_date) >= Number(commissionedDate);
    });
  
    // If found matching configs, return them
    if (filtered.length > 0) {
      return filtered;
    }
  
    // Fallback: highest version (pre-commission testing configs)
    let latestConfig = configHistory[0];

    for (const config of configHistory) {
        if ((config?.version ?? 0) > (latestConfig?.version ?? 0)) {
        latestConfig = config;
        }
    }

    return latestConfig ? [latestConfig] : [];
};

export interface GraphProps {
    closeGraphModal: () => void;
    sensorGroupName: string;
    sensorGroup: any;
}

const GraphModalGroup: React.FC<GraphProps> = (props: GraphProps) => {

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [dateFormat] = useState('MM-DD-YYYY')

    const [sensorGroup, setSensorGroup] = useState<any>(null);
    const [sensorboxes, setSensorboxes] = useState<Map<any, any>>(() => new Map());
    const [configHistories, setConfigHistories] = useState<any>([]);
    const [fieldTestOfflineDuration, setFieldTestOfflineDuration] = useState<any>(null);

    const [sensorboxOptions, setSensorboxOptions] = useState<any[]>([]);
    const [configOptions1, setConfigOptions1State] = useState<any>([]);
    const [configOptions2, setConfigOptions2State] = useState<any>([]);
    const [sensorOptions1, setSensorOptions1State] = useState<any>([]);
    const [sensorOptions2, setSensorOptions2State] = useState<any>([]);
    const [pinConfigByRepresentation, setPinConfigByRepresentation] = useState<Map<string, any>>(() => new Map());

    const [dateRange, setDateRange] = useState<any>(() => {
        const endDate = moment().unix();
        const startDate = moment().subtract(7, 'days').unix()
        return [startDate, endDate]
    })
    const [dateErrorState, setDateError] = useState<string>('');
    const [selectedSensorBox1, setSelectedSensorBox1] = useState<any>({_id: 0, name: 'Select a Sensorbox'});
    const [selectedSensorBox2, setSelectedSensorBox2] = useState<any>({_id: 0, name: 'Select a Sensorbox'});
    const [selectedConfig1, setSelectedConfig1] = useState<any>({_id: '', name: "Select Version"});
    const [selectedConfig2, setSelectedConfig2] = useState<any>({_id: '', name: "Select Version"});
    const [selectedParameter1, setSelectedParameter1] = useState<any>(null)
    const [selectedParameter2, setSelectedParameter2] = useState<any>(null)

    const [plotData, setPlotData] = useState(() => new Map())
    const [plots, setPlots] = useState<any>([])

    const [showThresholds1, setThresholds1] = useState<any>(false)
    const [showThresholds2, setThresholds2] = useState<any>(false)
    const [line1NoData, setLine1NoData] = useState<any>('')
    const [line2NoData, setLine2NoData] = useState<any>('')
    const [sensorGroupName, setSensorGroupName] = useState<string>();
    const [isFilterredData1, setIsFilterredData1] = useState<boolean>(true);
    const [isFilterredData2, setIsFilterredData2] = useState<boolean>(true);
    const [sensorboxTimezone, setSensorboxTimezone] = useState<string | null>(null);
    const [useLocationTimezone, setUseLocationTimezone] = useState<boolean>(false);
    const hvacCacheRef = useRef<Map<string, any[]>>(new Map());
    const weatherCacheRef = useRef<Map<string, any[]>>(new Map());
    useEffect(() => {
        setIsLoading(true)
        setSensorGroup(_.cloneDeep(props.sensorGroup));
        setSensorGroupName(props.sensorGroupName)
    }, []);

    useEffect(() => {
        getSensorboxes();
        Promise.all([getConfigHistory(), fetchSettings()]).then(() => setIsLoading(false))
    }, [sensorGroup]);

    useEffect(() => {
        setDateError(() => (datesValid() ? '' : dateError));
    }, [dateRange]);

    useEffect(() => {
        const configIndex = configHistories.length > 0 ? configHistories.findIndex((i: any[]) => i.some(x => x.sensorBox_id === selectedSensorBox1._id)) : -1;
        setConfigOptions1(configHistories[configIndex])
    }, [selectedSensorBox1])
    useEffect(() => {
        const configIndex = configHistories.length > 0 ? configHistories.findIndex((i: any[]) => i.some(x => x.sensorBox_id === selectedSensorBox2._id)) : -1;
        setConfigOptions2(configHistories[configIndex])
    }, [selectedSensorBox2])
    useEffect(() => {
        selectedConfig1 && setSensorOptions1(selectedConfig1)
    }, [selectedConfig1])
    useEffect(() => {
        selectedConfig2 && setSensorOptions2(selectedConfig2)
    }, [selectedConfig2])
    useEffect(() => {
        setSelectedParameter1(() => (sensorOptions1[0]))
    }, [sensorOptions1, selectedSensorBox1, selectedConfig1])
    useEffect(() => {
        setSelectedParameter2(() => (sensorOptions2[0]))
    }, [sensorOptions2, selectedSensorBox2, selectedConfig2])

    useEffect(() => {
        setDateError(() => (datesValid() ? '' : dateError));
        if (datesValid() && selectedSensorBox1._id !== 0 && selectedConfig1?._id && selectedParameter1?._id) {
            fetchData(sensorboxes.get(selectedSensorBox1._id), selectedConfig1, isFilterredData1, selectedParameter1._id)
        }
    }, [dateRange, selectedConfig1, selectedParameter1, isFilterredData1]);
    useEffect(() => {
        setDateError(() => (datesValid() ? '' : dateError));
        if (datesValid() && selectedSensorBox2._id !== 0 && selectedConfig2?._id && selectedParameter2?._id) {
            fetchData(sensorboxes.get(selectedSensorBox2._id), selectedConfig2, isFilterredData2, selectedParameter2._id)
        }
    }, [dateRange, selectedConfig2, selectedParameter2, isFilterredData2]);

    useEffect(() => {
        setPlots(() => preparePlots())
    }, [plotData, selectedConfig2, selectedConfig1, selectedParameter1, selectedParameter2, showThresholds2, showThresholds1])
    useEffect(() => {
        setThresholds1(false)
    }, [selectedParameter1])
    useEffect(() => {
        setThresholds2(false)
    }, [selectedParameter2])
//==================================================================

    const handleDateChange = (dates: any) => {
        if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
            return;
        }
        const prevStart = moment.unix(dateRange[0]);
        const startChanged = !dates[0].isSame(prevStart, 'day');
        if (startChanged) {
            const clampedEnd = clampEndDateForStart(dates[0].toDate(), dates[1].toDate());
            setDateRange([dates[0].unix(), moment(clampedEnd).unix()]);
            return;
        }
        const clampedStart = clampStartDateForEnd(dates[0].toDate(), dates[1].toDate());
        setDateRange([moment(clampedStart).unix(), dates[1].unix()]);
    };
    const disabledDate = (current: any) => {
        return current && current > moment().endOf('day');
    };
    const datesValid = () => {
        return isGraphDateRangeValid(moment.unix(dateRange[0]), moment.unix(dateRange[1]));
    }
    const handleFilterDataToggle1 = async () => {
        setIsFilterredData1(!isFilterredData1);
    }
    const handleFilterDataToggle2 = async () => {
        setIsFilterredData2(!isFilterredData2);
    }
    const getSensorboxes = () => {
        const sensorbBoxes = sensorboxes
        const options: { _id: any; name: any; }[] = []
        sensorGroup?.sensorboxes?.map((i: any) => {
            sensorbBoxes.set(i._id, i)
            options.push({_id: i._id, name: i.alias||i.sensor_box_name})
        })
        setSensorboxes(sensorbBoxes)
        setSensorboxOptions(options)
        
        // Extract timezone from first sensorbox
        if (sensorGroup?.sensorboxes?.length > 0) {
            const firstSensorbox = sensorGroup.sensorboxes[0]
            const timezone = firstSensorbox.timezone || null
            setSensorboxTimezone(timezone)
        }
    }
    const getConfigHistory = async () => {
        if (sensorGroup?._id) {
            const configHistories = await sensorService.getSensorBoxConfigHistoryForAsset(sensorGroup?._id)
            const pinConfigResp = await pinConfigurationService.getPinConfiguration();
            const { pin_configuration } = pinConfigResp.data;
            setPinConfigByRepresentation(pinConfigMapByRepresentation(pin_configuration || []));
            configHistories.forEach((config: any) => {
                config.forEach((config: any) => {
                    sensorGroup.sensorboxes.forEach((sensor: any) => {
                        if (sensor.sensor_box_name === config.sensorBox_name) {
                            if(sensor.sensor_box_source === 'external') {
                                    config.sensors.forEach((sensor: any) => {
                                    pin_configuration.forEach((pinConfig: any) => {
                                        if (pinConfig.representation === sensor.representation) {
                                        sensor.display_name = pinConfig.display_name
                                        }
                                    })
                                })
                            }
                        }
                    })
                })
            })
            const filteredConfigHistories = configHistories.map((configs: any[]) => {
                const sensorbox = sensorGroup?.sensorboxes?.find((sensor: any) => sensor.sensor_box_name === configs?.[0]?.sensorBox_name);
                return filterConfigHistoryByCommissionedDate(configs, sensorbox?.commissioned_date);
            })
            setConfigHistories(filteredConfigHistories)
        }
    }
    const setConfigOptions1 = (data: any) => {
        let configOptions1: any = []
        let configMap1: any = new Map()
        data?.forEach((config: any) => {
            configOptions1.push({
                _id: (config.version).toString(),
                name: `v${(config.version).toString()} (${moment.unix(config.start_date / 1000).format(dateFormat)} - ${config.end_date ? moment.unix(config.end_date / 1000).format(dateFormat) : 'Now'})`,
                sensors: config.sensors
            })
            configMap1.set(config.version, config)
        })
        setConfigOptions1State(() => configOptions1)
        setSelectedConfig1(() => (configOptions1[configOptions1.length - 1] || {_id: '', name: "Select Version"}))

    }
    const setConfigOptions2 = (data: any) => {
        let configOptions2: any = []
        data?.forEach((config: any) => {
            configOptions2.push({
                _id: (config.version).toString(),
                name: `v${(config.version).toString()}(${moment.unix(config.start_date / 1000).format(dateFormat)} - ${config.end_date ? moment.unix(config.end_date / 1000).format(dateFormat) : 'Now'})`,
                sensors: config.sensors
            })
        })
        setConfigOptions2State(() => configOptions2)
        setSelectedConfig2(() => configOptions2[configOptions2.length - 1] || {_id: '', name: "Select Version"})
    }
    const buildSensorOptionName = (sensor: any, configData: any, sensorbox: any) => {
        const suffix = configData?.sensors.filter((item: any) => item.representation === sensor.representation).length > 1
            ? ` - ${sensor?.sensor_name}`
            : '';
        const base = `${sensor.display_name}${suffix}`;
        const boxTempUnit = temperatureService.getTemperatureUnit(sensorbox?.asset_type);
        const unit = resolvePlotUnit(
            {
                representation: sensor.representation,
                type: sensor.type,
                pin_type: sensor.pin_type,
                sensor_name: sensor.sensor_name,
                unit: sensor.unit,
            },
            pinConfigByRepresentation,
            boxTempUnit
        );
        return formatLegendLabel(base, unit);
    };

    const setSensorOptions1 = (configData: any) => {
        if (selectedSensorBox1?._id === 0 || !configData?._id) {
            setSensorOptions1State([])
            return
        }
        const sensorbox = sensorboxes.get(selectedSensorBox1._id);
        const boxTempUnit = temperatureService.getTemperatureUnit(sensorbox?.asset_type);
        let sensorOptions1: any = []
        configData?.sensors?.forEach((sensor: any) => {
            sensorOptions1.push({
                _id: `${sensor.representation}_${sensor.sensor_name}`,
                name: buildSensorOptionName(sensor, configData, sensorbox),
                representation: sensor.representation
            })
        })
        sensorOptions1.push(
            {
                _id: 'outsideWeatherTemperature',
                name: formatLegendLabel('Outside Weather Temperature', resolvePlotUnit(
                    { representation: 'outsideWeatherTemperature', type: 'temperature' },
                    pinConfigByRepresentation,
                    boxTempUnit
                )),
                representation: 'outsideWeatherTemperature'
            },
            {
                _id: 'outsideWeatherHumidity',
                name: formatLegendLabel('Outside Weather Humidity', resolvePlotUnit(
                    { representation: 'outsideWeatherHumidity', type: 'humidity' },
                    pinConfigByRepresentation,
                    boxTempUnit
                )),
                representation: 'outsideWeatherHumidity'
            }
        )
        setSensorOptions1State(sensorOptions1)
    }
    const setSensorOptions2 = async (configData: any) => {
        if (selectedSensorBox2?._id === 0 || !configData?._id) {
            setSensorOptions2State([])
            return
        }
        const sensorbox = sensorboxes.get(selectedSensorBox2._id);
        const boxTempUnit = temperatureService.getTemperatureUnit(sensorbox?.asset_type);
        let sensorOptions2: any = []
        await configData?.sensors?.forEach((sensor: any) => {
            sensorOptions2.push({
                _id: `${sensor.representation}_${sensor.sensor_name}`,
                name: buildSensorOptionName(sensor, configData, sensorbox),
                representation: sensor.representation
            })
        })
        sensorOptions2.push(
            {
                _id: 'outsideWeatherTemperature',
                name: formatLegendLabel('Outside Weather Temperature', resolvePlotUnit(
                    { representation: 'outsideWeatherTemperature', type: 'temperature' },
                    pinConfigByRepresentation,
                    boxTempUnit
                )),
                representation: 'outsideWeatherTemperature'
            },
            {
                _id: 'outsideWeatherHumidity',
                name: formatLegendLabel('Outside Weather Humidity', resolvePlotUnit(
                    { representation: 'outsideWeatherHumidity', type: 'humidity' },
                    pinConfigByRepresentation,
                    boxTempUnit
                )),
                representation: 'outsideWeatherHumidity'
            }
        )
        setSensorOptions2State(sensorOptions2)
    }
    const handleConfig1Selection = async (e: any) => {
        if (selectedConfig1 !== e) {
            setSelectedConfig1(e)
        }
    }
    const handleConfig2Selection = async (e: any) => {
        if (selectedConfig2 !== e) {
            setSelectedConfig2(e)
        }
    }
    const getDatainChunks = async (sensorbox: any, config_id: any, fields?: string[]) => {
        let graphData: Array<any> = []
        let chunk: Array<any> = []
        let chunkLastTime = null
        const start = moment.unix(dateRange[0]).toDate()
        const end = moment.unix(dateRange[1]).toDate()
        while (true) {
            chunk = await sensorService.getGraphData(
                sensorbox,
                start,
                end,
                chunkLastTime,
                config_id,
                fields
            );
            if (chunk.length === 0) {
                break;
            }
            chunkLastTime = chunk[chunk.length - 1].added_time
            graphData = [...graphData, ...chunk]
        }
        return graphData
    }
    const fetchSettings = async () => {
        const settings = await settingsService.getSettings();
        setFieldTestOfflineDuration(settings?.data?.cycle_offline_duration)
    }
    const hvacCacheKey = (sensorBoxName: string, configId: any) => (
        `${sensorBoxName}|${configId}|${dateRange[0]}|${dateRange[1]}`
    )
    const weatherCacheKey = (sensorBoxName: string) => (
        `${sensorBoxName}|${dateRange[0]}|${dateRange[1]}`
    )
    const fetchData = async (sensorbox: any, selectedConfig: any, isFilterred: boolean, fieldId?: string) => {
        try {
            const isWeatherField = fieldId === 'outsideWeatherTemperature' || fieldId === 'outsideWeatherHumidity'
            const start = moment.unix(dateRange[0]).toDate()
            const end = moment.unix(dateRange[1]).toDate()
            if (isWeatherField) {
                const key = weatherCacheKey(sensorbox.sensor_box_name)
                let weatherObservations = weatherCacheRef.current.get(key)
                if (!weatherObservations) {
                    setIsLoading(true)
                    const weatherResp = await sensorService.getWeatherObservationsForSensorbox(
                        sensorbox.sensor_box_name,
                        start,
                        end
                    ).catch(() => [])
                    const rows: any[] = Array.isArray(weatherResp) ? weatherResp : (weatherResp?.data ?? [])
                    weatherObservations = rows
                    weatherCacheRef.current.set(key, rows)
                }
                prepareDataToPlotForSensorBox(sensorbox, [], isFilterred, weatherObservations, fieldId)
                return
            }

            const key = hvacCacheKey(sensorbox.sensor_box_name, selectedConfig._id)
            let data = hvacCacheRef.current.get(key)
            if (!data) {
                setIsLoading(true)
                data = await getDatainChunks(sensorbox.sensor_box_name, selectedConfig._id)
                hvacCacheRef.current.set(key, data)
                if (!(data.length > 0)) {
                    toast.error('No Records Found!')
                } else {
                    toast.success(`${data.length} Records Found!`)
                }
            }
            prepareDataToPlotForSensorBox(sensorbox, data, isFilterred, [], fieldId)
        } catch (error) {
            setIsLoading(false)
            toast.error('Network error')
        }
    }
    const prepareDataToPlotForSensorBox = async (sensorbox: any, dataL: any, isFilterred: boolean, weatherObservations: any[] = [], fieldId?: string) => {
        let y_data: any = {}, min: any = {}, max: any = {}, x_time: any = {}
        try {
            setIsLoading(true)
            const pinConfigResp = await pinConfigurationService.getPinConfiguration();
            const pinConfigurations = pinConfigResp?.data?.pin_configuration || [];
            const pinConfigMap = pinConfigMapByRepresentation(pinConfigurations);
            const boxTempUnit = temperatureService.getTemperatureUnit(sensorbox.asset_type);
            
            const temperatureAndHumidityConfigs = pinConfigurations.filter((config: any) => 
                config.type === 'temperature' || config.type === 'humidity'
            );

            const sensor_threshold: any = {};
            
            temperatureAndHumidityConfigs.forEach((config: any) => {
                if (config.type === 'temperature') {
                    const minF = config.representation.includes('room_temp') ? -130 : -50;
                    const maxF = config.representation.includes('room_temp') ? 120 : 300;
                    sensor_threshold[config.representation] = {
                        min: minF,
                        max: maxF,
                        minC: temperatureService.convertAndFormat(minF, 'C', 2),
                        maxC: temperatureService.convertAndFormat(maxF, 'C', 2)
                    };
                } else if (config.type === 'humidity') {
                    sensor_threshold[config.representation] = {
                        min: 0,
                        max: 100
                    };
                }
            });

            const {sensors, category} = sensorboxes.get(sensorbox._id)
            // Create a set of temperature sensor representations for quick lookup
            const temperatureSensorReps = new Set<string>();
            sensors?.forEach((sensor: any, index: number) => {
                sensor.pin_with_representation = `${sensor.representation}_${sensor.sensor_name}`
                if (fieldId && sensor.pin_with_representation !== fieldId) {
                    return
                }
                y_data[sensor.pin_with_representation] = []
                x_time[sensor.pin_with_representation] = []
                min[sensor.pin_with_representation] = Number.MAX_SAFE_INTEGER
                max[sensor.pin_with_representation] = Number.MIN_SAFE_INTEGER
                // Track temperature sensors
                if (sensor.pin_type === 'temperature' || sensor.sensor_name === 'sc' || sensor.sensor_name === 'sh') {
                    temperatureSensorReps.add(sensor.pin_with_representation);
                }
            });
            y_data.outsideWeatherTemperature = []
            x_time.outsideWeatherTemperature = []
            min.outsideWeatherTemperature = Number.MAX_SAFE_INTEGER
            max.outsideWeatherTemperature = Number.MIN_SAFE_INTEGER

            y_data.outsideWeatherHumidity = []
            x_time.outsideWeatherHumidity = []
            min.outsideWeatherHumidity = Number.MAX_SAFE_INTEGER
            max.outsideWeatherHumidity = Number.MIN_SAFE_INTEGER
            dataL?.forEach((item: any) => {
                Object.keys(y_data).forEach((representation) => {
                        if (item[representation] !== null) {
                            if (isFilterred) {
                                const matchingKey = Object.keys(sensor_threshold).find((key) =>
                                    representation.startsWith(key)
                                );
                                
                                const thresholdLimit = matchingKey ? sensor_threshold[matchingKey] : null;
                                let withinThreshold: boolean;
                                if (thresholdLimit && temperatureSensorReps.has(representation) && boxTempUnit === 'C' && thresholdLimit.minC != null) {
                                    const valueC = temperatureService.convertAndFormat(item[representation], 'C', 2);
                                    withinThreshold = valueC >= thresholdLimit.minC && valueC <= thresholdLimit.maxC;
                                } else {
                                    withinThreshold = thresholdLimit ? item[representation] >= thresholdLimit.min && item[representation] <= thresholdLimit.max : true;
                                }
                                if (withinThreshold) {
                                    // Convert temperature if needed
                                    let value = item[representation];
                                    if (temperatureSensorReps.has(representation) && typeof value === 'number') {
                                        value = boxTempUnit === 'C' 
                                            ? temperatureService.convertAndFormat(value, 'C', 2)
                                            : value;
                                    }
                                    min[representation] = min[representation] < value ? min[representation] : value
                                    max[representation] = max[representation] > value ? max[representation] : value
                                    y_data[representation].push(value)
                                    x_time[representation] = [...x_time[representation], item.added_time]
                                }
                            } else {
                                // Convert temperature if needed
                                let value = item[representation];
                                if (temperatureSensorReps.has(representation) && typeof value === 'number') {
                                    value = boxTempUnit === 'C' 
                                        ? temperatureService.convertAndFormat(value, 'C', 2)
                                        : value;
                                }
                                min[representation] = min[representation] < value ? min[representation] : value
                                max[representation] = max[representation] > value ? max[representation] : value
                                y_data[representation].push(value)
                                x_time[representation] = [...x_time[representation], item.added_time]
                            }
                        }
                    }
                )
            })
            weatherObservations.forEach((item: any) => {
                if (item?.time == null) {
                    return
                }

                const temperatureValue = parseFloat(item.temperature)
                if (!isNaN(temperatureValue)) {
                    const convertedTemperature = boxTempUnit === 'C'
                        ? temperatureService.convertAndFormat(temperatureValue, 'C', 2)
                        : temperatureValue
                    y_data.outsideWeatherTemperature.push(convertedTemperature)
                    min.outsideWeatherTemperature = Math.min(min.outsideWeatherTemperature, convertedTemperature)
                    max.outsideWeatherTemperature = Math.max(max.outsideWeatherTemperature, convertedTemperature)
                } else {
                    y_data.outsideWeatherTemperature.push(item.temperature)
                }
                x_time.outsideWeatherTemperature.push(item.time)

                const humidityValue = item.humidity != null ? Number(item.humidity) : null
                if (humidityValue != null && !isNaN(humidityValue)) {
                    y_data.outsideWeatherHumidity.push(humidityValue)
                    min.outsideWeatherHumidity = Math.min(min.outsideWeatherHumidity, humidityValue)
                    max.outsideWeatherHumidity = Math.max(max.outsideWeatherHumidity, humidityValue)
                } else {
                    y_data.outsideWeatherHumidity.push(item.humidity != null ? item.humidity : null)
                }
                x_time.outsideWeatherHumidity.push(item.time)
            })
            const PlotLines = new Map()
            const sensorboxName = sensorbox.alias || sensorbox.sensor_box_name || '';
            sensors?.forEach((sensor: any, index: number) => {
                if (fieldId && `${sensor.representation}_${sensor.sensor_name}` !== fieldId) {
                    return
                }
                let displayName = sensor.display_name || sensor.sensor_name
                if(sensorbox.sensor_box_source === 'external') {
                    pinConfigurations.forEach((pinConfig: any) => {
                        if (pinConfig.representation === sensor.representation) {
                            displayName = pinConfig.display_name
                        }
                    })
                }
                const plotUnit = resolvePlotUnit(
                    {
                        representation: sensor.representation,
                        pin_type: sensor.pin_type,
                        sensor_name: sensor.sensor_name,
                        unit: sensor.unit,
                    },
                    pinConfigMap,
                    boxTempUnit
                );
                const plotLine = {
                    x: x_time[sensor.pin_with_representation],
                    y: y_data[sensor.pin_with_representation],
                    min: min[sensor.pin_with_representation],
                    max: max[sensor.pin_with_representation],
                    mode: 'lines+markers',
                    type: 'scattergl',
                    name: formatLegendLabel(`${sensorboxName} - ${displayName}`, plotUnit),
                    sensor_type: sensor.pin_type,
                    category: category,
                    representation: sensor.representation
                }
                if (sensor.sensor_type?.min != null && sensor.sensor_type?.max != null) {
                    let minT = sensor.sensor_type.min;
                    let maxT = sensor.sensor_type.max;
                    // @ts-ignore
                    plotLine["thresholds"] = [minT, maxT];
                }
                PlotLines.set(sensor.pin_with_representation, plotLine)
            })
            PlotLines.set('outsideWeatherTemperature', {
                x: x_time.outsideWeatherTemperature,
                y: y_data.outsideWeatherTemperature,
                min: min.outsideWeatherTemperature,
                max: max.outsideWeatherTemperature,
                mode: 'lines+markers',
                type: 'scattergl',
                name: formatLegendLabel(`${sensorboxName} - Outside Weather Temperature`, boxTempUnit),
                sensor_type: 'temperature',
                category: category,
                representation: 'outsideWeatherTemperature'
            })
            PlotLines.set('outsideWeatherHumidity', {
                x: x_time.outsideWeatherHumidity,
                y: y_data.outsideWeatherHumidity,
                min: min.outsideWeatherHumidity,
                max: max.outsideWeatherHumidity,
                mode: 'lines+markers',
                type: 'scattergl',
                name: formatLegendLabel(
                    `${sensorboxName} - Outside Weather Humidity`,
                    resolvePlotUnit(
                        { representation: 'outsideWeatherHumidity', type: 'humidity' },
                        pinConfigMap,
                        boxTempUnit
                    )
                ),
                sensor_type: 'humidity',
                category: category,
                representation: 'outsideWeatherHumidity'
            })
            setPlotData((pd) => {
                pd.set(sensorbox._id, PlotLines);
                setIsLoading(false)
                return new Map(pd)
            })
        } catch (e) {
            console.error(e)
            setIsLoading(false)
        }
    }
    const closeModal = () => {
        props.closeGraphModal();
    }
    const preparePlots = () => {
        let lines = []
        const line1Source = plotData?.get(selectedSensorBox1._id)?.get(selectedParameter1?._id)
        const line1 = line1Source ? {...line1Source} : null
        if (line1) {
            line1.lineId = 0;
            line1.showThresholds = showThresholds1 && !!line1.thresholds
            line1.uid = `${selectedSensorBox1._id}_${selectedParameter1?._id}_0`
            lines.push(line1)
            setLine1NoData(null)
        } else {
            setLine1NoData('NO DATA')
        }
        const line2Source = plotData?.get(selectedSensorBox2?._id)?.get(selectedParameter2?._id)
        const line2 = line2Source ? {...line2Source} : null
        if (line2) {
            line2.lineId = 1;
            line2.showThresholds = showThresholds2 && !!line2.thresholds
            line2.uid = `${selectedSensorBox2._id}_${selectedParameter2?._id}_1`
            lines.push(line2)
            setLine2NoData(null)
        } else {
            setLine2NoData('NO DATA')
        }
        return lines
    }
    const isThresholdsSet = (id: number) => {
        return !!plots.find((x: any) => x.lineId === id)?.thresholds
    }
    let graphClassName = ['multiple-graph w-100 pt-3 pb-3']
    return (
        <div className="modal fade bd-example-modal-sm show" id="graphmodal" role="dialog"
             aria-labelledby="exampleModalLabel">
            <div className="modal-dialog modal-sensor-data-graph" role="document">
                <div className="modal-content">
                    <div className="modal-header ">
                        <h5 className="modal-title" id="exampleModalLabel">{
                            <div>{sensorGroupName} {sensorGroup?.alias &&
                                <span className='pl-2'>( {sensorGroup?.alias} )</span>}</div>}</h5>
                        <span aria-hidden="true" className="close" onClick={() => {
                            closeModal()
                        }}>&times;</span>
                    </div>
                    <div style={{marginBottom: '35px'}}></div>
                    <div className="row pt-40 mb-20 ml-4 mr-4 ht-custom" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap'}}>
                        <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '15px'}}>
                            <RangePicker
                                id="dateRangePicker"
                                size={'large'}
                                format={'MMM-DD-YYYY'}
                                defaultValue={[moment().subtract(7, 'days'), moment()]}
                                onChange={handleDateChange}
                                disabledDate={disabledDate}
                                allowClear={false}
                                value={dateRange.map((x: any) => moment.unix(x))}
                                mode={['date', 'date']}
                            />
                            <div className="error-msg">{dateErrorState}</div>
                        </div>
                        {sensorboxTimezone && (
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, marginLeft: 'auto' }}>
                                <Checkbox
                                    checked={useLocationTimezone}
                                    onChange={(e) => setUseLocationTimezone(e.target.checked)}
                                    className="custom-checkbox-teal"
                                >
                                    <span className="font-w-600">Show Asset Time</span>
                                </Checkbox>
                            </div>
                        )}
                    </div>
                    {
                        <Fragment>
                            <div className="row py-4 mt-30 mb-20 ml-2 mr-4 ht-custom" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap'}}>
                                <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp" style={{paddingRight: '15px'}}>
                                    <div className="form-group mb-0">
                                        <Select name="sensorbox1"
                                                data={sensorboxOptions}
                                                value={selectedSensorBox1}
                                                onChange={(e: any) => {
                                                    setSelectedSensorBox1(e)
                                                }}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp">
                                    <div className="form-group mb-0">
                                        <Select name="type"
                                                data={configOptions1}
                                                value={selectedConfig1}
                                                onChange={handleConfig1Selection}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp" style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingRight: '0'}}>
                                    <div className="form-group mb-0" style={{width: '100%'}}>
                                        <Select
                                            name="parameters"
                                            data={sensorOptions1}
                                            value={selectedParameter1}
                                            onChange={(e: any) => {
                                                const item = {...e, value: e._id, label: e.name, s: 1}
                                                setSelectedParameter1(item)
                                            }}

                                        />
                                        <div style={{display: 'flex', justifyContent:"space-between",alignItems: 'center', gap: '10px', marginTop:8}}>
                                            <Checkbox
                                                checked={showThresholds1}
                                                style={{display: isThresholdsSet(0) ? '' : 'none'}}
                                                onChange={(e) => setThresholds1(e.target.checked)}
                                                className="custom-checkbox-teal"
                                            ><span className="font-w-600">Show Thresholds</span></Checkbox>
                                            <div style={{display: isThresholdsSet(0) ? 'flex' : 'none', alignItems: 'center', gap: '8px'}}>
                                                <span className="font-w-600">Data: </span>
                                                <Tooltip title={isFilterredData1 ? "Show Raw Data" : "Show Clean Data"} placement="left">
                                                    <div className={`toggle-switch ${isFilterredData1 ? "on" : "off"}`}
                                                        onClick={handleFilterDataToggle1}>
                                                        <div className="toggle-handle"></div>
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {line1NoData && (<div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp">
                                    <div className="d-flex mb-0 align-items-center"
                                         style={{minHeight: '38px', color: 'red'}}>
                                        <span>{line1NoData}</span>
                                    </div>
                                </div>)}
                            </div>
                            <div className="row mt-30 mb-20 ml-2 mr-4 ht-custom" style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap'}}>
                                <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp" style={{paddingRight: '15px'}}>
                                    <div className="form-group mb-0">
                                        <Select name="sensorbox1"
                                                data={sensorboxOptions}
                                                value={selectedSensorBox2}
                                                onChange={(e: any) => {
                                                    setSelectedSensorBox2(e)
                                                }}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp">
                                    <div className="form-group mb-0">
                                        <Select name="type"
                                                data={configOptions2}
                                                value={selectedConfig2}
                                                onChange={handleConfig2Selection}
                                        />
                                    </div>
                                </div>
                                <div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp" style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', paddingRight: '0'}}>
                                    <div className="form-group mb-0" style={{width: '100%'}}>
                                        <Select
                                            name="parameters"
                                            data={sensorOptions2}
                                            value={selectedParameter2}
                                            onChange={(e: any) => {
                                                const item = {...e, value: e._id, label: e.name, s: 2}
                                                setSelectedParameter2(item)
                                            }}
                                        />
                                        <div style={{display: 'flex', justifyContent:"space-between",alignItems: 'center', gap: '10px', marginTop:8}}>
                                            <Checkbox
                                                name="showThresholds2"
                                                checked={showThresholds2}
                                                style={{display: isThresholdsSet(1) ? '' : 'none'}}
                                                onChange={(e) => setThresholds2(e.target.checked)}
                                                className="custom-checkbox-teal"
                                            ><span className="font-w-600">Show Thresholds</span></Checkbox>
                                            <div style={{display: isThresholdsSet(1) ? 'flex' : 'none', alignItems: 'center', gap: '8px'}}>
                                                <span className="font-w-600">Data: </span>
                                                <Tooltip title={isFilterredData2 ? "Show Raw Data" : "Show Clean Data"} placement="left">
                                                    <div className={`toggle-switch ${isFilterredData2 ? "on" : "off"}`}
                                                        onClick={handleFilterDataToggle2}>
                                                        <div className="toggle-handle"></div>
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {line2NoData && (<div className="col-xs-4 col-sm-4 col-md-3 col-lg-3 media-margin-tp">
                                    <div className="d-flex mb-0 align-items-center"
                                         style={{minHeight: '38px', color: 'red'}}>
                                        <span>{line2NoData}</span>
                                    </div>
                                </div>)}
                            </div>
                        </Fragment>
                    }
                    <div className="modal-body modal-height-graph px-3">
                        <div className="d-flex flex-wrap w-100 ml-0">
                            {
                                sensorGroup?.sensorboxes?.length > 0 ?
                                    <>
                                        <div className={graphClassName.join(' ')}>
                                            <div className='border p-3 h-100'>
                                                {
                                                    plots.length > 0 ?
                                                        <Graph
                                                            key={0}
                                                            fieldTestOfflineDuration={fieldTestOfflineDuration}
                                                            plots={plots}
                                                            showThresholds1={showThresholds1}
                                                            showThresholds2={showThresholds2}
                                                            sensorGroup={props.sensorGroup}
                                                            timezone={useLocationTimezone ? sensorboxTimezone : null}
                                                        />
                                                        :
                                                        <div className="no-data-message w-100 col-12">
                                                            No Data Available,Choose different configurations to plot
                                                            the graph. </div>
                                                }
                                            </div>
                                        </div>
                                    </>
                                    : <div className="no-data-message w-100 col-12">No Data Available</div>}
                        </div>
                    </div>
                    <div className="modal-footer modal-footer-custom-padding">
                    </div>
                </div>
            </div>
            {isLoading ?
                <LoadingWheel/>
                : null}
        </div>
    );
};
export default GraphModalGroup